import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiTrendingUp, FiUser, FiCalendar } from 'react-icons/fi';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import MessageBox from '../../components/MessageBox';
import AssetSaleService from '../../services/assetSale.service';
import AssetSaleInfo from '../AssetSales/AssetSaleInfo';
import AssetSalesDebts from '../AssetSales/AssetSalesDebts';
import HistoryModal from '../../components/HistoryModal';
import SingleObjectDocumentManager from '../../components/SingleObjectDocumentManager/SingleObjectDocumentManager';
import CommissionList from '../Commissions/CommissionList';
import Balance from '../Balance/Balance';
import { getError, showCurrency } from '../../utils';
import { isBalanceEnabled, isCommissionsEnabled } from '../../utils/featureFlags';
import GenerateContractModal from '../../components/GenerateContractModal/GenerateContractModal';
import { useLabels } from '../../hooks/useLabels';
import { EntityList, DetailHeader, TabNav, Button } from '../../components/EntityList';

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true };
	case 'FETCH_SUCCESS':
		return { ...state, assetSale: action.payload, loading: false };
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	default:
		return state;
	}
};

const emptyTabState = { rows: [], loading: true, error: '' };

function formatDate(date) {
	return date ? new Date(date).toLocaleDateString('es-UY') : '';
}

function formatMoney(currency, amount) {
	return `${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();
}

export default function AssetSaleDetail() {
	const { t } = useLabels();
	const params = useParams();
	const navigate = useNavigate();

	const { id, tab } = params;
	const [{ assetSale, loading, error }, dispatch] = useReducer(reducer, {
		assetSale: {},
		loading: true,
		error: '',
	});

	const [debtsState, setDebtsState] = useState(emptyTabState);
	const [showHistoryLog, setShowHistoryLog] = useState(false);
	const [showGeneratePdf, setShowGeneratePdf] = useState(false);

	const fetchData = async () => {
		dispatch({ type: 'FETCH_REQUEST' });
		try {
			const result = await AssetSaleService.getDetails(id);
			dispatch({ type: 'FETCH_SUCCESS', payload: result.data });
		} catch (error) {
			dispatch({ type: 'FETCH_FAIL', payload: getError(error) });
		}
	};

	const fetchDebts = useCallback(async () => {
		setDebtsState((prev) => ({ ...prev, loading: true }));
		try {
			const { data } = await AssetSaleService.getDebtsForAssetSale(id);
			setDebtsState({ rows: data, loading: false, error: '' });
		} catch (error) {
			setDebtsState({ rows: [], loading: false, error: getError(error) });
		}
	}, [id]);

	useEffect(() => {
		fetchData();
		fetchDebts();
	}, [id]);

	async function editAssetSaleHandler() {
		navigate(`/AdminScreen/assetSales/editAssetSale/${assetSale._id}`, {
			state: { returnTo: `/AdminScreen/assetSales/assetSale/${id}/datos` },
		});
	}

	const tabs = [
		{ id: 'datos', label: t('title.details') },
		{ id: 'deudas', label: t('entity.debt.plural'), count: debtsState.rows.length > 0 ? debtsState.rows.length : undefined },
		...(isBalanceEnabled() ? [{ id: 'balance', label: t('title.balance') }] : []),
		...(isCommissionsEnabled() ? [{ id: 'comisiones', label: t('title.commissions') }] : []),
		{ id: 'documentos', label: t('title.documents') },
	];
	const TAB_SLUGS = tabs.map((tabItem) => tabItem.id);
	const currentTab = TAB_SLUGS.includes(tab) ? tab : 'datos';

	useEffect(() => {
		if (!loading && tab !== currentTab) {
			navigate(`/AdminScreen/assetSales/assetSale/${id}/${currentTab}`, { replace: true });
		}
	}, [loading, tab, currentTab, id]);

	const statusLabel = assetSale.sumDebts > 0
		? t('field.withBalance')
		: assetSale.sumDebts === 0
			? t('field.settled')
			: null;
	const statusTone = assetSale.sumDebts > 0 ? 'warn' : 'ok';

	const subline = [
		assetSale.purchaser?.name && { label: t('entity.purchaser'), icon: <FiUser />, value: assetSale.purchaser.name, strong: true },
		assetSale.amount && {
			label: t('field.amount'),
			icon: <FiTrendingUp />,
			value: <span className='tw:font-medium tw:text-cat-3'>{formatMoney(assetSale.currency, assetSale.amount)}</span>,
		},
		assetSale.date && { label: t('field.date'), icon: <FiCalendar />, value: formatDate(assetSale.date) },
		assetSale.lot && { label: t('field.lotUpper'), value: <span className='tw:font-mono'>p. {assetSale.lot}</span> },
	].filter(Boolean);

	return loading ? (
		<EntityList>
			<DetailPageLoader entityLabel={t('entity.assetSale')} />
		</EntityList>
	) : error ? (
		<MessageBox variant='danger'>{error}</MessageBox>
	) : (
		<EntityList>
			<Helmet>
				<title>{`${t('entity.assetSale')}: ${assetSale.customId || ''}`}</title>
			</Helmet>

			<div className='tw:mb-4 tw:flex tw:items-center tw:gap-1.5 tw:text-[12.5px] tw:text-ink-3'>
				<span className='tw:cursor-pointer tw:hover:text-ink-1' onClick={() => navigate('/AdminScreen/assetSales')}>
					{t('entity.assetSale.plural')}
				</span>
				<span>/</span>
				<span className='tw:text-ink-1'>{assetSale.customId}</span>
			</div>

			<DetailHeader
				name={`${t('title.saleTo')} ${assetSale.purchaser?.name || t('entity.purchaser')} · ${assetSale.asset?.name || t('entity.assetSentence')}`}
				kicker={`${t('entity.assetSaleOf')} · ${assetSale.customId || ''}`}
				statusLabel={statusLabel}
				statusTone={statusTone}
				subline={subline}
				avatar={
					<span className='tw:flex tw:h-[72px] tw:w-[72px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-[14px] tw:bg-cat-3 tw:text-white tw:shadow-sm'>
						<FiTrendingUp size={28} />
					</span>
				}
				actions={
					<>
						<Button variant='default' onClick={() => navigate(-1)}>{t('action.backPlain')}</Button>
						<Button onClick={() => setShowGeneratePdf(true)}>{t('action.generateContract')}</Button>
						<Button onClick={() => setShowHistoryLog(true)}>{t('title.changeHistory')}</Button>
						{!assetSale.asset && (
							<Button variant='primary' onClick={editAssetSaleHandler}>
								{t('action.edit')}
							</Button>
						)}
					</>
				}
			/>

			<div className='tw:mt-5'>
				<TabNav
					tabs={tabs}
					current={currentTab}
					onChange={(nextTab) => navigate(`/AdminScreen/assetSales/assetSale/${id}/${nextTab}`)}
				/>
			</div>

			<div className='tw:mt-6'>
				{currentTab === 'datos' && <AssetSaleInfo assetSale={assetSale} />}
				{currentTab === 'deudas' && <AssetSalesDebts {...debtsState} onRefetch={fetchDebts} />}
				{currentTab === 'balance' && isBalanceEnabled() && <Balance type='assetSale' data={assetSale} />}
				{currentTab === 'comisiones' && isCommissionsEnabled() && (
					<CommissionList assetSaleId={id} parentAmount={assetSale.amount} parentCurrency={assetSale.currency} />
				)}
				{currentTab === 'documentos' && (
					<SingleObjectDocumentManager objectId={id} objectType='assetSale' documentType='assetSale' />
				)}
			</div>

			<HistoryModal model='AssetSale' show={showHistoryLog} handleClose={() => setShowHistoryLog(false)} id={id} isHistory={true} />

			{showGeneratePdf && (
				<GenerateContractModal
					show={showGeneratePdf}
					onHide={() => setShowGeneratePdf(false)}
					entityType='assetSale'
					entityId={id}
				/>
			)}
		</EntityList>
	);
}
